import { useStore } from '@nanostores/react'
import { Fit, Layout, useRive } from '@rive-app/react-canvas'
import { Dialog } from 'radix-ui'
import { useState } from 'react'
import bgImg from '~/assets/images/bg.svg'
import anim from '~/assets/rive/subscribed.riv'
import { motion } from '~/lib/motion'
import { m } from '~/paraglide/messages'
import { platform } from '~/params'
import { $subscribedView } from '~/store/subscribed-view'

export function SubscribedView(props: { onComplete: () => void }) {
  const { isOpen, isTrial } = useStore($subscribedView)
  const [isBtnRevealed, setIsBtnRevealed] = useState(false)
  const isChinaAndroid = platform === 'china_android'

  const { RiveComponent } = useRive({
    src: anim,
    artboard: 'paywall_success',
    stateMachines: ['State Machine 1'],
    autoplay: true,
    layout: new Layout({ fit: Fit.FitHeight }),
  })

  const title = () => {
    if (!isChinaAndroid) {
      return m.onboarding_paywall_subscribed_success_title()
    }
    if (isTrial) {
      return m.onboarding_paywall_subscribed_success_title_trial_cna()
    }
    return m.onboarding_paywall_subscribed_success_title_cna()
  }

  const content = () => {
    if (!isChinaAndroid) {
      return m.onboarding_paywall_subscribed_success_content()
    }
    return m.onboarding_paywall_subscribed_success_content_trial_cna()
  }

  return (
    <Dialog.Root open={isOpen}>
      <Dialog.Portal>
        <Dialog.Content
          className="fixed inset-0 z-[100]"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <motion.div
            className="size-full bg-[#001721] px-8 pt-[calc(60px+var(--safe-area-top))] text-center text-[#F3FEFF]"
            style={{
              backgroundImage: `url(${bgImg})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="h-[30vh] w-full">
              <RiveComponent className="size-full" />
            </div>

            <Dialog.Title asChild>
              <motion.div
                className="mt-6 whitespace-pre-line text-[36px] font-bold"
                style={{
                  textShadow: '0 0 10px rgba(255, 255, 255, 0.5)',
                }}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 3,
                  opacity: {
                    ease: [0.33, 1, 0.68, 1],
                    duration: 0.6,
                  },
                  y: {
                    ease: [0.5, 1, 0.89, 1],
                    duration: 0.6,
                  },
                }}
              >
                {title()}
              </motion.div>
            </Dialog.Title>

            <Dialog.Description asChild>
              <motion.div
                className="mt-2 flex h-[112px] items-center justify-center whitespace-pre-line text-[20px] font-bold"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 3.5,
                  opacity: {
                    ease: [0.33, 1, 0.68, 1],
                    duration: 0.6,
                  },
                  y: {
                    ease: [0.5, 1, 0.89, 1],
                    duration: 0.6,
                  },
                }}
              >
                {content()}
              </motion.div>
            </Dialog.Description>
          </motion.div>

          <motion.div
            className="absolute bottom-12 left-0 right-0 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              delay: 4.5,
              ease: [0.33, 1, 0.68, 1],
              duration: 0.3,
              onComplete: () => {
                setIsBtnRevealed(true)
              },
            }}
          >
            <button
              className="group h-[52px] w-full rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
              disabled={!isBtnRevealed}
              onClick={props.onComplete}
            >
              <span className="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
                {m.onboarding_paywall_subscribed_success_btn()}
              </span>
            </button>
          </motion.div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
